import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TestCategory } from "types/tests/core";
import { calculateAgeForTest, displayPatientName, formatLongDate } from "@utils/CommonUtil";
import useTestList from "@hooks/useTestList";
import {
  AXIS_KEYS,
  AggregateInput,
  AxisKey,
  PerTestDetail,
  ProfessionMatch,
  TEST_CATEGORY_TO_AXIS,
  aggregateAxis,
  topProfessions,
} from "@utils/cognitiveProfile";

export interface CognitiveProfilePatientData {
  name: string;
  age: number | null;
  testDate: string;
}

export interface CognitiveProfileTestIds {
  wcstUuid?: string;
  raUuid?: string;
  dcUuid?: string;
  cptUuid?: string;
  omUuid?: string;
}

const TEST_CATEGORIES: TestCategory[] = [
  TestCategory.WisconsinCardSorting,
  TestCategory.ReadingAssessment,
  TestCategory.Dyscalculia,
  TestCategory.ContinuousPerformance,
  TestCategory.OcularMotility,
];

const TOP_STRENGTH_COUNT = 3;
const TOP_PROFESSION_COUNT = 5;
// TODO(SD-837): DEFAULT_AGE_FOR_NORMS is a fallback used when no patient DOB
// is present. Once the band tables in normativeBands.ts are age-stratified,
// this default will quietly skew scores for patients with missing DOBs.
// Either require DOB upstream or surface "no age, scores not normalised" in
// the UI when this fallback is applied.
const DEFAULT_AGE_FOR_NORMS = 15;

const unwrapList = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  if (
    raw &&
    typeof raw === "object" &&
    "data" in raw &&
    Array.isArray((raw as { data: unknown[] }).data)
  ) {
    return (raw as { data: unknown[] }).data;
  }
  return [];
};

interface MatchedTestDocument {
  results?: unknown;
  taskResults?: unknown;
  wcstData?: unknown;
  startDate?: string;
  patient?: { name?: string; dob?: string };
}

const pickTestForCategory = (
  list: unknown,
  category: TestCategory,
): MatchedTestDocument | undefined => {
  const items = unwrapList(list) as Array<{ category?: TestCategory }>;
  const match = items.find((t) => t.category === category);
  return match as MatchedTestDocument | undefined;
};

const computePatientData = (test: {
  patient?: { name?: string; dob?: string };
  startDate?: string;
}): CognitiveProfilePatientData | null => {
  if (!test.startDate) return null;
  const startDate = new Date(test.startDate);
  return {
    name: test.patient?.name ? displayPatientName(test.patient.name) : "Patient",
    // Age is null when DOB is unknown so the header can render "—" instead of "0 years old".
    // Scoring still falls back to DEFAULT_AGE_FOR_NORMS via the `?? DEFAULT_AGE_FOR_NORMS` below.
    age: test.patient?.dob ? calculateAgeForTest(startDate, new Date(test.patient.dob)) : null,
    testDate: formatLongDate(startDate),
  };
};

interface FetchedTest {
  category: TestCategory;
  uuid: string;
  list: unknown;
  loading: boolean;
}

export interface CognitiveProfileData {
  patientData: CognitiveProfilePatientData;
  axes: { key: AxisKey; score: number | null }[];
  topStrengths: { key: AxisKey; score: number }[];
  topMatches: ProfessionMatch[];
  missingTests: TestCategory[];
  perTestDetail: PerTestDetail[];
  loading: boolean;
}

export function useCognitiveProfileData(
  overrideIds?: CognitiveProfileTestIds,
): CognitiveProfileData {
  const [searchParams] = useSearchParams();

  const ids: Record<TestCategory, string | null> = {
    [TestCategory.WisconsinCardSorting]: overrideIds?.wcstUuid ?? searchParams.get("wcstUuid"),
    [TestCategory.ReadingAssessment]: overrideIds?.raUuid ?? searchParams.get("raUuid"),
    [TestCategory.Dyscalculia]: overrideIds?.dcUuid ?? searchParams.get("dcUuid"),
    [TestCategory.ContinuousPerformance]: overrideIds?.cptUuid ?? searchParams.get("cptUuid"),
    [TestCategory.OcularMotility]: overrideIds?.omUuid ?? searchParams.get("omUuid"),
  } as Record<TestCategory, string | null>;

  const wcstId = ids[TestCategory.WisconsinCardSorting];
  const raId = ids[TestCategory.ReadingAssessment];
  const dcId = ids[TestCategory.Dyscalculia];
  const cptId = ids[TestCategory.ContinuousPerformance];
  const omId = ids[TestCategory.OcularMotility];

  // Skip the network call when the corresponding axis has no UUID — otherwise
  // useTestList would fall back to fetching the full test list for that user.
  const wcst = useTestList(wcstId ? { uuid: wcstId } : {}, { skip: !wcstId });
  const ra = useTestList(raId ? { uuid: raId } : {}, { skip: !raId });
  const dc = useTestList(dcId ? { uuid: dcId } : {}, { skip: !dcId });
  const cpt = useTestList(cptId ? { uuid: cptId } : {}, { skip: !cptId });
  const om = useTestList(omId ? { uuid: omId } : {}, { skip: !omId });

  const fetched: FetchedTest[] = [
    {
      category: TestCategory.WisconsinCardSorting,
      uuid: ids[TestCategory.WisconsinCardSorting] ?? "",
      list: wcst.testList,
      loading: wcst.testListLoading,
    },
    {
      category: TestCategory.ReadingAssessment,
      uuid: ids[TestCategory.ReadingAssessment] ?? "",
      list: ra.testList,
      loading: ra.testListLoading,
    },
    {
      category: TestCategory.Dyscalculia,
      uuid: ids[TestCategory.Dyscalculia] ?? "",
      list: dc.testList,
      loading: dc.testListLoading,
    },
    {
      category: TestCategory.ContinuousPerformance,
      uuid: ids[TestCategory.ContinuousPerformance] ?? "",
      list: cpt.testList,
      loading: cpt.testListLoading,
    },
    {
      category: TestCategory.OcularMotility,
      uuid: ids[TestCategory.OcularMotility] ?? "",
      list: om.testList,
      loading: om.testListLoading,
    },
  ];

  const anyRequested = fetched.some((f) => f.uuid !== "");
  const anyLoading = fetched.some((f) => f.uuid !== "" && f.loading);

  const [patientData, setPatientData] = useState<CognitiveProfilePatientData>({
    name: "Patient",
    age: null,
    testDate: formatLongDate(new Date()),
  });

  const matchedTests = fetched.map((f) => ({
    category: f.category,
    test: f.uuid ? pickTestForCategory(f.list, f.category) : undefined,
  }));

  useEffect(() => {
    for (const m of matchedTests) {
      if (m.test) {
        const next = computePatientData(m.test);
        if (next) {
          setPatientData(next);
          return;
        }
      }
    }
    // matchedTests identity changes whenever any test's data updates — we depend on the underlying lists below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wcst.testList, ra.testList, dc.testList, cpt.testList, om.testList]);

  const perTestDetail: PerTestDetail[] = [];
  const presentCategories = new Set<TestCategory>();

  for (const m of matchedTests) {
    if (!m.test) continue;
    const aggregateInput: AggregateInput = {
      category: m.category,
      test: m.test,
      age: patientData.age ?? DEFAULT_AGE_FOR_NORMS,
    };
    const detail = aggregateAxis(aggregateInput);
    if (!detail) continue;
    perTestDetail.push(detail);
    presentCategories.add(m.category);
  }

  const axisScoreByKey: Map<AxisKey, number | null> = new Map();
  for (const detail of perTestDetail) {
    axisScoreByKey.set(detail.axis, detail.score);
  }

  const axes = AXIS_KEYS.map((key) => ({
    key,
    score: axisScoreByKey.has(key) ? (axisScoreByKey.get(key) ?? null) : null,
  }));

  const topStrengths = axes
    .filter((a): a is { key: AxisKey; score: number } => a.score !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, TOP_STRENGTH_COUNT);

  const missingTests = TEST_CATEGORIES.filter((c) => !presentCategories.has(c));

  const topMatches = topProfessions({ axisScores: axisScoreByKey }, TOP_PROFESSION_COUNT);

  return {
    patientData,
    axes,
    topStrengths,
    topMatches,
    missingTests,
    perTestDetail,
    loading: anyRequested && anyLoading,
  };
}

export const COGNITIVE_PROFILE_AXES: readonly AxisKey[] = AXIS_KEYS;
export { TEST_CATEGORY_TO_AXIS };
